// B2 login page (T04 §9.5): email + password through Better Auth. Labels are the Playwright locators
// (getByLabel("Email") / getByLabel("Password") / getByRole("button", { name: "Sign in" })).
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Button } from "@chartvn/ui/button";
import { Input } from "@chartvn/ui/input";
import { StatusRegion } from "@chartvn/ui/components/status-region/status-region";
import { BuildMarker } from "@chartvn/ui/components/build-marker/build-marker";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { log } from "../telemetry";
import { useBuildInfo } from "../features/shell/use-build-info";

const searchSchema = z.object({
  redirect: z.string().optional(),
  reason: z.enum(["idle", "expired", "locked"]).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect, reason } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const form = new FormData(e.currentTarget);
    const { error: err } = await authClient.signIn.email(
      { email: formString(form, "email"), password: formString(form, "password") },
      {
        onSuccess: (ctx) => {
          const data = ctx.data as { twoFactorRedirect?: boolean } | undefined;
          if (data?.twoFactorRedirect) void navigate({ to: "/two-factor" });
          else
            void navigate({ to: redirect && redirect.startsWith("/") ? redirect : "/playground" });
        },
      },
    );
    setPending(false);
    if (err) {
      log.debug("sign-in rejected", err.status); // status only; never the email or password
      setError("Email or password is incorrect.");
    }
  }

  const build = useBuildInfo();

  return (
    // A4, the focused-task archetype (03-FOUNDATIONS-LAYOUT-A11Y-CONTENT.md §2.6): a single centred
    // column no wider than 720 px, one primary action, and nothing competing with it. The 44 px
    // comfortable control size applies here rather than the 32 px clinician compact size, because
    // sign-in is reached from any device including a phone at a bedside.
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center gap-6 p-6">
        <h1 className="text-2xl font-semibold">Sign in to ChartVN</h1>
        {/* One live region rather than an ad-hoc <p role="status">, so a later message cannot compete
          with this one for the same announcement. */}
        {reason === "locked" && (
          <StatusRegion
            tone="moderate"
            message="Your session is locked. Sign in to return to where you were."
            className="rounded-md px-4"
          />
        )}
        {reason === "idle" && (
          <StatusRegion
            tone="moderate"
            message="You were signed out after 15 minutes without activity."
            className="rounded-md px-4"
          />
        )}
        <form
          onSubmit={(e) => void onSubmit(e)}
          aria-label="Sign in"
          className="flex flex-col gap-4"
        >
          {/* The label text is the Playwright locator and must not be renamed (see the file header). */}
          <label className="flex flex-col gap-1 text-sm">
            Email
            <Input name="email" type="email" autoComplete="username" required className="h-11" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Password
            <Input
              name="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={15}
              className="h-11"
            />
          </label>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <Button type="submit" disabled={pending} data-telemetry="login-submit" className="h-11">
            Sign in
          </Button>
        </form>
      </main>
      {/* Which build this is, before anyone signs in (J-048). `/healthz` is public, so the version
          the running server reports is readable here too. */}
      <BuildMarker
        version={build.version}
        release={build.release}
        environment={build.environment}
        pending={build.pending}
      />
    </div>
  );
}
